// src/pages/LatestNews/LatestNews.jsx
import React, { useEffect, useMemo, useState } from "react";
import { getLatestNews } from "../../services/newsServices";
import { useUser } from "../Home/UserContext";
import { toAbsolutePhotoUrl } from "../../services/newsPhotoServices";
import SectionTitle from "../../components/ui/SectionTitle";
import {
  FiChevronLeft,
  FiChevronRight,
  FiMaximize2,
  FiX,
  FiFileText,
} from "react-icons/fi";

const FALLBACK = "/img/no-image.jpg";

function cn(...c) {
  return c.filter(Boolean).join(" ");
}

const LatestNews = () => {
  const [newsList, setNewsList] = useState([]);
  const [index, setIndex] = useState(0);

  const [selectedNews, setSelectedNews] = useState(null); // modal article
  const [selectedImage, setSelectedImage] = useState(null); // modal image
  const [animDir, setAnimDir] = useState("next"); // "next" | "prev"

  const { user } = useUser(); // si tu veux l’utiliser plus tard

  useEffect(() => {
    (async () => {
      try {
        const latest = await getLatestNews();
        const active = (latest || []).filter((n) => n.is_active);
        setNewsList(active.slice(0, 6)); // ✅ 3 -> 6 pour que la navigation ait du sens (ajuste si tu veux)
        setIndex(0);
      } catch (e) {
        console.error("Failed to fetch news", e);
        setNewsList([]);
      }
    })();
  }, []);

  const toAbs = (url) => {
    const u = (url || "").trim();
    const abs = u ? toAbsolutePhotoUrl(u) : "";
    return abs || FALLBACK;
  };

  const onImgError = (e) => {
    if (e?.currentTarget) e.currentTarget.src = FALLBACK;
  };

  const total = newsList.length;
  const current = useMemo(() => newsList[index] || null, [newsList, index]);

  const canPrev = total > 1;
  const canNext = total > 1;

  const go = (nextIndex, direction) => {
    if (!total) return;
    setAnimDir(direction);
    setIndex((nextIndex + total) % total);
  };

  const prev = () => go(index - 1, "prev");
  const next = () => go(index + 1, "next");

  const openArticle = () => current && setSelectedNews(current);
  const closeArticle = () => setSelectedNews(null);

  const openImageModal = () => current && setSelectedImage(toAbs(current.image_url));
  const closeImageModal = () => setSelectedImage(null);

  const authorLabel = (n) => {
    const p = n?.author?.prenom || "";
    const nom = n?.author?.nom || "";
    const s = `${p} ${nom}`.trim();
    return s || "Club";
  };

  const safePreviewHtml = useMemo(() => {
    if (!current?.content) return "";
    return current.content;
  }, [current?.content]);

  return (
    <section
      className={cn(
        "py-12 sm:py-16",
        "bg-lightBackground dark:bg-darkBackground",
        "text-black dark:text-white"
      )}
      id="latest-news"
    >
      {/* CSS local pour animations (sans Tailwind config) */}
      <style>{`
        @keyframes ln-in-next { 
          from { opacity: 0; transform: translateX(18px); } 
          to   { opacity: 1; transform: translateX(0); } 
        }
        @keyframes ln-in-prev { 
          from { opacity: 0; transform: translateX(-18px); } 
          to   { opacity: 1; transform: translateX(0); } 
        }
        @media (prefers-reduced-motion: reduce) {
          .ln-anim { animation: none !important; transition: none !important; }
        }
      `}</style>

      <div className="mx-auto max-w-7xl px-4">
        <SectionTitle
          title="Dernières Actualités"
          subtitle={total ? "Affiche les dernières infos du club" : "Aucune actualité pour le moment"}
        />

        {/* Wrapper */}
        <div className="mt-10">
          {!current ? (
            <div
              className={cn(
                "rounded-3xl p-8 text-center",
                "border border-slate-200/70 dark:border-slate-800",
                "bg-white/70 dark:bg-slate-900/50 backdrop-blur shadow-sm"
              )}
            >
              <p className="text-slate-600 dark:text-slate-300">
                Aucune actualité disponible.
              </p>
            </div>
          ) : (
            <div
              className={cn(
                "relative overflow-hidden rounded-3xl",
                "border border-slate-200/70 dark:border-slate-800",
                "bg-white/80 dark:bg-slate-900/60 backdrop-blur",
                "shadow-sm"
              )}
            >
              {/* Nav buttons (desktop) */}
              <button
                type="button"
                onClick={prev}
                disabled={!canPrev}
                className={cn(
                  "hidden md:grid absolute left-3 top-1/2 -translate-y-1/2 z-10",
                  "h-11 w-11 place-items-center rounded-full",
                  "bg-white/80 dark:bg-slate-950/60 backdrop-blur",
                  "border border-slate-200/70 dark:border-slate-800",
                  "text-slate-800 dark:text-slate-100",
                  "shadow hover:shadow-md transition",
                  "disabled:opacity-40 disabled:cursor-not-allowed"
                )}
                aria-label="Actualité précédente"
              >
                <FiChevronLeft className="text-xl" />
              </button>

              <button
                type="button"
                onClick={next}
                disabled={!canNext}
                className={cn(
                  "hidden md:grid absolute right-3 top-1/2 -translate-y-1/2 z-10",
                  "h-11 w-11 place-items-center rounded-full",
                  "bg-white/80 dark:bg-slate-950/60 backdrop-blur",
                  "border border-slate-200/70 dark:border-slate-800",
                  "text-slate-800 dark:text-slate-100",
                  "shadow hover:shadow-md transition",
                  "disabled:opacity-40 disabled:cursor-not-allowed"
                )}
                aria-label="Actualité suivante"
              >
                <FiChevronRight className="text-xl" />
              </button>

              {/* Content */}
              <div
                key={current.id}
                className={cn(
                  "ln-anim",
                  "grid md:grid-cols-12 gap-0",
                  animDir === "next" ? "[animation:ln-in-next_.28s_ease-out]" : "[animation:ln-in-prev_.28s_ease-out]"
                )}
              >
                {/* Image */}
                <div className="md:col-span-5 relative">
                  <button
                    type="button"
                    onClick={openImageModal}
                    className="block w-full"
                    aria-label="Agrandir l'image"
                    title="Cliquer pour agrandir"
                  >
                    <img
                      src={toAbs(current.image_url)}
                      alt={current.title || "Illustration"}
                      className="h-64 md:h-full w-full object-cover"
                      onError={onImgError}
                      loading="lazy"
                      decoding="async"
                    />
                  </button>

                  {/* Bouton zoom discret */}
                  <button
                    type="button"
                    onClick={openImageModal}
                    className={cn(
                      "absolute right-3 top-3",
                      "h-10 w-10 rounded-full grid place-items-center",
                      "bg-white/80 dark:bg-slate-950/60 backdrop-blur",
                      "border border-slate-200/70 dark:border-slate-800",
                      "text-slate-800 dark:text-slate-100",
                      "shadow hover:shadow-md transition"
                    )}
                    aria-label="Zoom image"
                    title="Zoom"
                  >
                    <FiMaximize2 />
                  </button>
                </div>

                {/* Texte */}
                <div className="md:col-span-7 p-5 sm:p-7">
                  {/* meta */}
                  <div className="flex items-center justify-between gap-3">
                    <div className="inline-flex items-center rounded-full bg-slate-100 dark:bg-slate-800/60 px-3 py-1 text-xs font-semibold text-slate-700 dark:text-slate-200 border border-slate-200/70 dark:border-slate-800">
                      {authorLabel(current)}
                    </div>

                    <div className="text-xs text-slate-500 dark:text-slate-300">
                      {index + 1}/{total}
                    </div>
                  </div>

                  <h3 className="mt-4 text-2xl font-extrabold tracking-tight text-slate-900 dark:text-slate-50 leading-snug">
                    {current.title}
                  </h3>

                  {/* aperçu */}
                  <div className="mt-4">
                    <div
                      className={cn(
                        "prose prose-slate dark:prose-invert max-w-none",
                        "text-[15px] leading-relaxed",
                        "line-clamp-6",
                        // ✅ force les gras à être lisibles en dark
                        "dark:[&_strong]:!text-white dark:[&_b]:!text-white",
                        "dark:[&_strong]:font-extrabold dark:[&_b]:font-extrabold"
                      )}
                      dangerouslySetInnerHTML={{ __html: safePreviewHtml }}
                    />
                  </div>

                  {/* actions */}
                  <div className="mt-6 flex items-center justify-between gap-3">
                    {/* Mobile nav */}
                    <div className="md:hidden flex gap-2">
                      <button
                        type="button"
                        onClick={prev}
                        className={cn(
                          "h-10 w-10 rounded-full grid place-items-center",
                          "bg-white/80 dark:bg-slate-950/60 backdrop-blur",
                          "border border-slate-200/70 dark:border-slate-800",
                          "text-slate-800 dark:text-slate-100",
                          "shadow hover:shadow-md transition"
                        )}
                        aria-label="Actualité précédente"
                      >
                        <FiChevronLeft />
                      </button>
                      <button
                        type="button"
                        onClick={next}
                        className={cn(
                          "h-10 w-10 rounded-full grid place-items-center",
                          "bg-white/80 dark:bg-slate-950/60 backdrop-blur",
                          "border border-slate-200/70 dark:border-slate-800",
                          "text-slate-800 dark:text-slate-100",
                          "shadow hover:shadow-md transition"
                        )}
                        aria-label="Actualité suivante"
                      >
                        <FiChevronRight />
                      </button>
                    </div>

                    <button
                      type="button"
                      onClick={openArticle}
                      className={cn(
                        "ml-auto inline-flex items-center gap-2",
                        "rounded-full px-4 py-2 text-sm font-semibold",
                        "border border-primary/30 text-primary bg-primary/10 hover:bg-primary/15",
                        "transition"
                      )}
                      aria-label="Lire l'article complet"
                    >
                      <FiFileText />
                      Lire la suite
                    </button>
                  </div>

                  {/* dots */}
                  {total > 1 ? (
                    <div className="mt-6 flex items-center justify-center gap-2">
                      {newsList.map((n, i) => (
                        <button
                          key={n.id}
                          onClick={() => go(i, i > index ? "next" : "prev")}
                          className={cn(
                            "h-2.5 w-2.5 rounded-full transition",
                            i === index
                              ? "bg-primary"
                              : "bg-slate-300 dark:bg-slate-700 hover:bg-slate-400 dark:hover:bg-slate-600"
                          )}
                          aria-label={`Aller à l'actualité ${i + 1}`}
                        />
                      ))}
                    </div>
                  ) : null}
                </div>
              </div>

              {/* Accent bas */}
              <div
                aria-hidden
                className="h-1.5 w-full opacity-70"
                style={{
                  background:
                    "linear-gradient(to right, transparent, var(--tw-color-primary, #2563eb), transparent)",
                }}
              />
            </div>
          )}
        </div>

        {/* MODALE article complet */}
        {selectedNews && (
          <div
            className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-3 sm:p-6"
            onClick={closeArticle}
            role="dialog"
            aria-modal="true"
          >
            <div
              className={cn(
                "relative w-full max-w-5xl overflow-hidden rounded-3xl",
                "bg-white/90 dark:bg-slate-950/80 backdrop-blur",
                "border border-slate-200/70 dark:border-slate-800",
                "shadow-2xl"
              )}
              onClick={(e) => e.stopPropagation()}
            >
              <button
                className={cn(
                  "absolute right-4 top-4 z-10",
                  "h-10 w-10 rounded-full grid place-items-center",
                  "bg-white/80 dark:bg-slate-950/60 backdrop-blur",
                  "border border-slate-200/70 dark:border-slate-800",
                  "text-slate-800 dark:text-slate-100",
                  "shadow hover:shadow-md transition"
                )}
                onClick={closeArticle}
                aria-label="Fermer"
                type="button"
              >
                <FiX className="text-xl" />
              </button>

              <div className="grid md:grid-cols-12">
                <div className="md:col-span-5">
                  <img
                    src={toAbs(selectedNews.image_url)}
                    alt={selectedNews.title || "Illustration"}
                    className="h-64 md:h-full w-full object-cover"
                    onError={onImgError}
                  />
                </div>

                <div className="md:col-span-7 p-5 sm:p-7">
                  <div className="inline-flex items-center rounded-full bg-slate-100 dark:bg-slate-800/60 px-3 py-1 text-xs font-semibold text-slate-700 dark:text-slate-200 border border-slate-200/70 dark:border-slate-800">
                    {authorLabel(selectedNews)}
                  </div>

                  <h2 className="mt-4 text-2xl font-extrabold text-slate-900 dark:text-slate-50">
                    {selectedNews.title}
                  </h2>

                  <div className="mt-4 max-h-[55vh] overflow-y-auto pr-2">
                    <div
                      className={cn(
                        "prose prose-slate dark:prose-invert max-w-none",
                        "text-[15px] leading-relaxed",
                        "dark:[&_strong]:!text-white dark:[&_b]:!text-white",
                        "dark:[&_strong]:font-extrabold dark:[&_b]:font-extrabold"
                      )}
                      dangerouslySetInnerHTML={{ __html: selectedNews.content }}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* MODALE zoom image seule */}
        {selectedImage && (
          <div
            className="fixed inset-0 z-50 bg-black/75 backdrop-blur-sm flex items-center justify-center px-3"
            onClick={closeImageModal}
            role="dialog"
            aria-modal="true"
          >
            <div
              className="relative w-full max-w-6xl"
              onClick={(e) => e.stopPropagation()}
            >
              <img
                src={selectedImage}
                alt="Agrandissement"
                className="w-full max-h-[75vh] object-contain rounded-2xl shadow-2xl bg-black/20"
                onError={onImgError}
              />
              <button
                className="absolute -top-3 -right-3 h-11 w-11 rounded-full bg-white/90 text-slate-900 text-2xl grid place-items-center shadow-lg hover:text-primary transition"
                onClick={closeImageModal}
                aria-label="Fermer"
                type="button"
              >
                &times;
              </button>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default LatestNews;
